import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Save, Camera, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, gbp } from "../lib/api";

const inputCls = "h-10 px-2 rounded-md border border-sand bg-white text-sm focus:outline-none focus:ring-2 focus:ring-tea-hover/70 w-full";
// Reserves 2 lines of height so a label that wraps ("Packaging £/portion") doesn't push its
// input lower than shorter labels ("Portions made") sharing the same grid row.
const fieldLabelCls = "block min-h-8 text-xs uppercase tracking-[0.2em] font-medium text-ink/50";
const UNITS = ["kg", "L", "each"];

// Single source of truth for a recipe line's cost, so sundry amortization (uses divisor)
// can't silently drift out of sync across the cost panel, scale preview, and per-line display.
function lineCost(ing, quantityPerPortion) {
  if (!ing) return 0;
  const yf = ing.yield_factor || 1;
  const uses = ing.category === "sundry" ? (ing.expected_uses || 1) : 1;
  return (parseFloat(quantityPerPortion) || 0) * ((ing.latest_unit_price || 0) / yf / uses);
}

export default function RecipeEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [phase, setPhase] = useState(isNew ? "choose" : "form");
  const [ingredients, setIngredients] = useState([]);
  const [settings, setSettings] = useState({ labour_rate_per_hour: 15 });
  const [form, setForm] = useState({ name: "", portion_count: 10, labour_minutes: 60, packaging_cost: 0, notes: "", lines: [], menu_price: "", target_food_cost_pct: "" });
  // Snapshot of the recipe as loaded from the server — the scale preview scales from this
  // fixed baseline, independent of whatever the user is mid-editing in `form`.
  const [savedRecipe, setSavedRecipe] = useState(null);
  const [scaleTo, setScaleTo] = useState("");
  // Labour/packaging don't scale automatically with batch size (a bigger batch doesn't take
  // proportionally longer, and packaging is already a per-portion figure) — left blank so the
  // scale panel visibly falls back to the saved recipe's own values (shown as placeholders)
  // until the user reviews and, if needed, overrides them for the new batch size.
  const [scaleLabourMinutes, setScaleLabourMinutes] = useState("");
  const [scalePackagingCost, setScalePackagingCost] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/ingredients").then((r) => setIngredients(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
    if (!isNew) api.get(`/recipes/${id}`).then((r) => {
      setForm({ ...r.data, lines: r.data.lines.map((l) => ({ ...l, _key: crypto.randomUUID() })) });
      setSavedRecipe(r.data);
    });
  }, [id, isNew]);

  const ingMap = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);

  const costing = useMemo(() => {
    let ingredientCost = 0;
    for (const l of form.lines) {
      ingredientCost += lineCost(ingMap[l.ingredient_id], l.quantity_per_portion);
    }
    const portions = Math.max(parseInt(form.portion_count) || 1, 1);
    const labour = ((parseFloat(form.labour_minutes) || 0) / 60) * (settings.labour_rate_per_hour || 0) / portions;
    const packaging = parseFloat(form.packaging_cost) || 0;
    const total = ingredientCost + labour + packaging;
    const menuPrice = parseFloat(form.menu_price) || 0;
    const foodCostPct = menuPrice > 0 ? (total / menuPrice) * 100 : null;
    return { ingredientCost, labour, packaging, total, foodCostPct };
  }, [form, ingMap, settings]);

  // Ingredients priced at £0 (freshly created from an unmatched scan line, never priced yet)
  // silently understate the cost panel above with no indication once the recipe is saved —
  // the amber row treatment only exists pre-save. Surface it here too.
  const zeroPricedCount = useMemo(() => {
    let n = 0;
    for (const l of form.lines) {
      const ing = ingMap[l.ingredient_id];
      if (ing && (ing.is_new || Number(ing.latest_unit_price) === 0)) n++;
    }
    return n;
  }, [form.lines, ingMap]);

  // Live "what if" preview, from a fixed snapshot of the saved recipe (never `form`, so mid-edits
  // can't leak in). quantity_per_portion is invariant to portion count by definition — it's the
  // recipe's per-portion DNA, not a per-batch total — so it is never multiplied by any ratio here;
  // only the batch/shopping-list total (quantity_per_portion * target) changes with target portions.
  // Ingredient cost per portion is therefore identical to the saved recipe's own cost per portion;
  // only labour (a fixed total time diluted over more/fewer portions) and, if the user chooses to
  // override it, packaging actually change with target portion count.
  const scaled = useMemo(() => {
    if (!savedRecipe) return null;
    const target = parseFloat(scaleTo);
    if (!target || target <= 0) return null;
    let ingredientCost = 0;
    const lines = savedRecipe.lines.map((l) => {
      const ing = ingMap[l.ingredient_id];
      ingredientCost += lineCost(ing, l.quantity_per_portion);
      return {
        ingredient_id: l.ingredient_id, name: ing?.name || "Unknown ingredient", unit: ing?.canonical_unit || "",
        perPortion: l.quantity_per_portion, batchTotal: l.quantity_per_portion * target,
      };
    });
    const labourMinutes = scaleLabourMinutes === "" ? (savedRecipe.labour_minutes || 0) : (parseFloat(scaleLabourMinutes) || 0);
    const packaging = scalePackagingCost === "" ? (savedRecipe.packaging_cost || 0) : (parseFloat(scalePackagingCost) || 0);
    const labour = (labourMinutes / 60) * (settings.labour_rate_per_hour || 0) / target;
    return { target, lines, labourMinutes, packaging, ingredientCost, labour, costPerPortion: ingredientCost + labour + packaging };
  }, [savedRecipe, scaleTo, scaleLabourMinutes, scalePackagingCost, ingMap, settings]);

  const setLine = (i, key, value) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [key]: value };
    setForm({ ...form, lines });
  };

  const onScanFile = async (file) => {
    if (!file) return;
    setPhase("scanning");
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/recipes/scan", body);
      setForm((f) => ({
        ...f,
        name: data.name || f.name,
        portion_count: data.portion_count || f.portion_count,
        // Only an exact name match is trusted and applied directly. A fuzzy (non-exact) match is
        // never silently applied — it's carried as a suggestion (suggested_id) so the same amber
        // review row as a true non-match shows, and the user has to explicitly confirm it.
        lines: data.lines.map((l) => {
          const exact = l.match_type === "exact";
          return {
            _key: crypto.randomUUID(),
            ingredient_id: exact ? (l.ingredient_id || "") : "",
            quantity_per_portion: l.quantity,
            name: exact ? undefined : l.name,
            unit: l.unit,
            confidence: l.confidence,
            suggested_id: l.match_type === "fuzzy" ? l.ingredient_id : undefined,
          };
        }),
      }));
      setPhase("form");
      if (data.lines.some((l) => l.match_type !== "exact"))
        toast.message("Some ingredients didn't match your existing store exactly · review the highlighted lines before saving.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPhase("choose");
    }
  };

  // Trial recipe cap is an expected, honest boundary, not a mistake the user made, so it's
  // surfaced as an upgrade nudge with a real action, not a red error toast that just dead-ends.
  const handleSaveError = (err) => {
    const detail = err.response?.data?.detail;
    if (detail?.code === "trial_recipe_cap") {
      toast.info(formatApiErrorDetail(detail), {
        action: { label: "Upgrade", onClick: () => navigate("/pricing") },
        duration: 8000,
      });
      return;
    }
    toast.error(formatApiErrorDetail(detail) || err.message);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give your dish a name");
    setBusy(true);
    try {
      const payload = {
        name: form.name, portion_count: parseInt(form.portion_count) || 1,
        labour_minutes: parseFloat(form.labour_minutes) || 0,
        packaging_cost: parseFloat(form.packaging_cost) || 0,
        notes: form.notes || "",
        lines: form.lines.filter((l) => l.ingredient_id || l.name).map((l) => ({
          ingredient_id: l.ingredient_id || null,
          quantity_per_portion: parseFloat(l.quantity_per_portion) || 0,
          name: l.ingredient_id ? null : l.name,
          unit: l.ingredient_id ? null : (l.unit || "each"),
        })),
        menu_price: form.menu_price === "" ? null : parseFloat(form.menu_price),
        target_food_cost_pct: form.target_food_cost_pct === "" ? null : parseFloat(form.target_food_cost_pct),
      };
      if (isNew) await api.post("/recipes", payload);
      else await api.put(`/recipes/${id}`, payload);
      toast.success("Recipe saved");
      navigate("/recipes");
    } catch (err) {
      handleSaveError(err);
    } finally {
      setBusy(false);
    }
  };

  const saveScaled = async () => {
    if (!scaled) return;
    setBusy(true);
    try {
      const payload = {
        name: `${savedRecipe.name} (${scaled.target} portions)`,
        portion_count: scaled.target,
        // Reviewed/edited (or explicitly left as the original) values from the panel, not silently
        // carried forward unscaled — see the scaleLabourMinutes/scalePackagingCost state above.
        labour_minutes: scaled.labourMinutes,
        packaging_cost: scaled.packaging,
        notes: savedRecipe.notes || "",
        // quantity_per_portion is copied UNCHANGED — it's per-portion and therefore invariant to
        // portion count by definition. Only portion_count (and, above, labour/packaging) differ
        // for the new recipe; the ingredient list and its ratios are identical to the original.
        lines: savedRecipe.lines.map((l) => ({
          ingredient_id: l.ingredient_id,
          quantity_per_portion: l.quantity_per_portion,
        })),
        menu_price: null,
        target_food_cost_pct: savedRecipe.target_food_cost_pct,
      };
      const { data } = await api.post("/recipes", payload);
      toast.success("Scaled recipe saved as a new dish");
      navigate(`/recipes/${data.id}`);
    } catch (err) {
      handleSaveError(err);
    } finally {
      setBusy(false);
    }
  };

  if (phase === "choose")
    return (
      <div className="space-y-6 animate-rise" data-testid="recipe-editor-choose">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">New dish</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-1">Build a recipe</h1>
        </div>
        <button data-testid="recipe-scan-button" onClick={() => fileRef.current?.click()}
          className="w-full rounded-lg border-2 border-dashed border-clay/50 bg-surface hover:border-tea-hover hover:bg-tea/10 transition-colors duration-200 p-12 sm:p-20 flex flex-col items-center gap-4">
          <span className="w-16 h-16 rounded-full bg-tea text-tea-text flex items-center justify-center"><Camera size={28} /></span>
          <div className="text-center">
            <p className="font-heading font-semibold text-xl">Scan a recipe page</p>
            <p className="text-sm text-ink/50 mt-1">Photo or PDF from your recipe book · we'll pull out the title, yield and ingredients</p>
          </div>
        </button>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden"
          data-testid="recipe-scan-file-input" onChange={(e) => onScanFile(e.target.files?.[0])} />

        <div className="flex items-center gap-3 text-ink/40">
          <div className="flex-1 h-px bg-sand" />
          <span className="text-xs uppercase tracking-[0.2em]">or</span>
          <div className="flex-1 h-px bg-sand" />
        </div>

        <button data-testid="recipe-start-blank-button" onClick={() => setPhase("form")}
          className="w-full h-12 rounded-lg border border-sand text-ink/70 hover:bg-sand transition-colors duration-200 font-medium">
          Start from scratch
        </button>
      </div>
    );

  if (phase === "scanning")
    return (
      <div className="py-24 flex flex-col items-center gap-4 animate-rise" data-testid="recipe-scanning-state">
        <Loader2 className="animate-spin text-tea-text" size={40} />
        <p className="font-heading font-semibold text-xl">Reading your recipe…</p>
        <p className="text-sm text-ink/50">Extracting the title, yield and ingredients.</p>
      </div>
    );

  return (
    <div className="space-y-6 animate-rise" data-testid="recipe-editor-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">{isNew ? "New dish" : "Edit dish"}</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-1">{isNew ? "Build a recipe" : form.name}</h1>
      </div>

      {zeroPricedCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-warnamber bg-warnamber/10 p-3 text-sm text-ink/80" data-testid="zero-price-warning">
          <AlertTriangle size={16} className="text-warnamber shrink-0" />
          <span>
            {zeroPricedCount} ingredient{zeroPricedCount !== 1 ? "s" : ""} in this recipe {zeroPricedCount !== 1 ? "have" : "has"} no price yet &middot; cost per portion may be understated.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Dish name</label>
            <input data-testid="recipe-name-input" className={`${inputCls} mt-1 h-12`} value={form.name} placeholder="e.g. Lamb tagine with couscous"
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={fieldLabelCls}>Portions made</label>
              <input type="number" min="1" data-testid="recipe-portions-input" className={`${inputCls} mt-1 font-mono-num`} value={form.portion_count}
                onChange={(e) => setForm({ ...form, portion_count: e.target.value })} />
            </div>
            <div>
              <label className={fieldLabelCls}>Labour (min)</label>
              <input type="number" min="0" data-testid="recipe-labour-input" className={`${inputCls} mt-1 font-mono-num`} value={form.labour_minutes}
                onChange={(e) => setForm({ ...form, labour_minutes: e.target.value })} />
            </div>
            <div>
              <label className={fieldLabelCls}>Packaging £/portion</label>
              <input type="number" step="0.01" min="0" data-testid="recipe-packaging-input" className={`${inputCls} mt-1 font-mono-num`} value={form.packaging_cost}
                onChange={(e) => setForm({ ...form, packaging_cost: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Menu price £/portion</label>
              <input type="number" step="0.01" min="0" placeholder="optional" data-testid="recipe-menu-price-input" className={`${inputCls} mt-1 font-mono-num`} value={form.menu_price}
                onChange={(e) => setForm({ ...form, menu_price: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Target food cost %</label>
              <input type="number" step="1" min="0" max="100" placeholder="optional" data-testid="recipe-target-food-cost-input" className={`${inputCls} mt-1 font-mono-num`} value={form.target_food_cost_pct}
                onChange={(e) => setForm({ ...form, target_food_cost_pct: e.target.value })} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Ingredients (per portion)</label>
              <button data-testid="recipe-add-line-button"
                onClick={() => setForm({ ...form, lines: [...form.lines, { _key: crypto.randomUUID(), ingredient_id: "", quantity_per_portion: 0.1 }] })}
                className="flex items-center gap-1 text-sm text-tea-text font-medium hover:underline"><Plus size={15} /> Add ingredient</button>
            </div>
            {form.lines.length === 0 && (
              <p className="text-sm text-ink/40 bg-surface border border-dashed border-sand rounded-lg p-4">No ingredients yet. Add from your store, and yield factors apply automatically.</p>
            )}
            <div className="space-y-2">
              {form.lines.map((line, i) => {
                const ing = ingMap[line.ingredient_id];
                const isUnmatchedScan = !line.ingredient_id && line.name;
                const thisLineCost = lineCost(ing, line.quantity_per_portion);
                const defaultProduct = ing?.products?.find((product) => product.is_default);
                const cheaperProduct = defaultProduct && ing.products
                  .filter((product) => !product.is_default && product.latest_unit_price < defaultProduct.latest_unit_price)
                  .sort((a, b) => a.latest_unit_price - b.latest_unit_price)[0];
                return (
                  <div key={line._key} className={`rounded-lg ${isUnmatchedScan ? "border border-warnamber bg-warnamber/10 p-2" : ""}`} data-testid={`recipe-line-${i}`}>
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <select data-testid={`recipe-line-ingredient-${i}`} className={`${inputCls} col-span-6`} value={line.ingredient_id || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({ ...form, lines: form.lines.map((l, j) => j === i ? { ...l, ingredient_id: val } : l) });
                        }}>
                        <option value="">{isUnmatchedScan ? `+ New: ${line.name}` : "Choose ingredient…"}</option>
                        {ingredients.map((ing) => <option key={ing.id} value={ing.id}>{ing.name} ({gbp(ing.latest_unit_price)}/{ing.canonical_unit}){ing.products?.length ? ` · ${ing.products.length} product${ing.products.length === 1 ? "" : "s"}` : ""}</option>)}
                      </select>
                      <input type="number" step="any" data-testid={`recipe-line-qty-${i}`} className={`${inputCls} col-span-3 font-mono-num`} value={line.quantity_per_portion}
                        onChange={(e) => setLine(i, "quantity_per_portion", e.target.value)} />
                      <span className="col-span-2 text-right font-mono-num text-sm">{gbp(thisLineCost)}</span>
                      <button data-testid={`recipe-line-delete-${i}`} onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}
                        className="col-span-1 p-2 text-ink/40 hover:text-tea-text"><Trash2 size={15} /></button>
                    </div>
                    {isUnmatchedScan && (
                      <div className="flex items-center gap-2 mt-2 pl-1">
                        <AlertTriangle size={13} className="text-warnamber shrink-0" />
                        <input data-testid={`recipe-line-new-name-${i}`} className={`${inputCls} h-8 text-xs flex-1`} value={line.name}
                          placeholder="New ingredient name" onChange={(e) => setLine(i, "name", e.target.value)} />
                        <select data-testid={`recipe-line-new-unit-${i}`} className={`${inputCls} h-8 text-xs w-20`} value={line.unit || "each"}
                          onChange={(e) => setLine(i, "unit", e.target.value)}>
                          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                    )}
                    {isUnmatchedScan && line.suggested_id && ingMap[line.suggested_id] && (
                      <div className="flex items-center gap-1.5 mt-1 pl-6 text-xs">
                        <span className="text-ink/50">Possible match:</span>
                        <button type="button" data-testid={`recipe-line-suggested-${i}`}
                          className="text-tea-text font-medium hover:underline"
                          onClick={() => setForm({ ...form, lines: form.lines.map((l, j) => j === i ? { ...l, ingredient_id: line.suggested_id } : l) })}>
                          {ingMap[line.suggested_id].name} &middot; use this
                        </button>
                      </div>
                    )}
                    {cheaperProduct && (
                      <Link to="/ingredients" className="block mt-1 pl-1 text-xs text-tea-text hover:underline" data-testid={`recipe-line-cheaper-product-${i}`}>
                        Currently costed on {defaultProduct.name} &middot; {gbp(defaultProduct.latest_unit_price - cheaperProduct.latest_unit_price)} cheaper option available
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-ink/40 mt-2">Quantity is in the ingredient's canonical unit (kg / L / each). Amber rows are new ingredients from a scan &middot; not yet in your store.</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Notes</label>
            <textarea data-testid="recipe-notes-input" rows={2} className="w-full mt-1 p-3 rounded-md border border-sand bg-white text-sm focus:outline-none focus:ring-2 focus:ring-tea-hover/70"
              value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          {!isNew && savedRecipe && (
            <div className="bg-surface border border-sand rounded-lg p-5 space-y-4" data-testid="scale-panel">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">Scale preview</p>
                  <p className="text-sm text-ink/60 mt-0.5">
                    Batch totals for a different portion count. Ingredient cost per portion doesn't change when you scale &middot; this is a preview, it doesn't change the saved recipe.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <label className="text-xs text-ink/50" htmlFor="scale-to-input">Scale to</label>
                  <input id="scale-to-input" type="number" min="1" data-testid="scale-portions-input" className={`${inputCls} w-24 font-mono-num`}
                    value={scaleTo} placeholder={String(savedRecipe.portion_count)} onChange={(e) => setScaleTo(e.target.value)} />
                  <span className="text-xs text-ink/50">portions</span>
                </div>
              </div>
              {scaled && (
                <>
                  <div className="space-y-1.5" data-testid="scaled-lines">
                    {scaled.lines.map((l) => (
                      <div key={l.ingredient_id} className="flex justify-between text-sm">
                        <span className="text-ink/70">{l.name}</span>
                        <span className="font-mono-num">
                          {l.batchTotal.toFixed(3)} {l.unit} <span className="text-ink/40 text-xs">total</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3 border-t border-sand pt-3">
                    <div>
                      <label className="text-xs text-ink/50">Labour (min) for this batch &middot; review</label>
                      <input type="number" min="0" data-testid="scale-labour-input" className={`${inputCls} mt-1 font-mono-num`}
                        value={scaleLabourMinutes} placeholder={String(savedRecipe.labour_minutes)}
                        onChange={(e) => setScaleLabourMinutes(e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs text-ink/50">Packaging £/portion &middot; review</label>
                      <input type="number" step="0.01" min="0" data-testid="scale-packaging-input" className={`${inputCls} mt-1 font-mono-num`}
                        value={scalePackagingCost} placeholder={String(savedRecipe.packaging_cost)}
                        onChange={(e) => setScalePackagingCost(e.target.value)} />
                    </div>
                  </div>
                  <p className="text-[11px] text-ink/40">
                    Labour minutes and packaging don't scale automatically with batch size &middot; the values above default to the saved recipe's own figures until you change them.
                  </p>
                  <div className="flex items-center justify-between border-t border-sand pt-3">
                    <span className="text-sm text-ink/60">Cost per portion at {scaled.target} portions</span>
                    <span className="font-mono-num text-xl font-semibold text-tea-text" data-testid="scaled-cost-per-portion">{gbp(scaled.costPerPortion)}</span>
                  </div>
                  <button data-testid="save-scaled-recipe-button" onClick={saveScaled} disabled={busy}
                    className="w-full h-11 rounded-lg border border-tea text-tea-text hover:bg-tea/10 font-medium transition-colors duration-200 disabled:opacity-60">
                    Save as new recipe ({scaled.target} portions)
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-24 h-fit bg-ink text-white rounded-lg p-6 space-y-4" data-testid="recipe-cost-panel">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-white/60">True cost per portion</p>
          <p className="font-mono-num text-4xl font-semibold text-tea" data-testid="recipe-cost-per-portion">{gbp(costing.total)}</p>
          <div className="space-y-2 text-sm border-t border-white/20 pt-4">
            <div className="flex justify-between"><span className="text-white/70">Ingredients (after yield)</span><span className="font-mono-num">{gbp(costing.ingredientCost)}</span></div>
            <div className="flex justify-between"><span className="text-white/70">Labour @ {gbp(settings.labour_rate_per_hour)}/hr</span><span className="font-mono-num">{gbp(costing.labour)}</span></div>
            <div className="flex justify-between"><span className="text-white/70">Packaging</span><span className="font-mono-num">{gbp(costing.packaging)}</span></div>
            {costing.foodCostPct != null && (
              <div className="flex justify-between" data-testid="recipe-food-cost-pct">
                <span className="text-white/70">Food cost</span>
                <span className="font-mono-num">
                  {costing.foodCostPct.toFixed(1)}%
                  {form.target_food_cost_pct && ` (target ${form.target_food_cost_pct}%)`}
                </span>
              </div>
            )}
          </div>
          <button data-testid="recipe-save-button" onClick={save} disabled={busy}
            className="w-full h-12 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60">
            <Save size={17} /> {busy ? "Saving…" : "Save recipe"}
          </button>
        </div>
      </div>
    </div>
  );
}
